import { useEffect, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";
import Table from "../../components/Tables/Table";
import { Drawer, Form, Modal, Space, Tag, Tooltip, Divider } from "antd";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";
import TableHeader from "../../components/Tables/TableHeader";
import TextInput from "../../components/Inputs/TextInput";
import FormButton from "../../components/Buttons/FormButton";
import { Formik } from "formik";
import FormCancelButton from "../../components/Buttons/FormCancelButton";
import FormErrors from "../../components/Errors/FormErrors";
import DeleteModal from "../../components/Modals/DeleteModal";
import { IoIosWarning } from "react-icons/io";
import CustomDropDownInput from "../../components/Inputs/CustomDropDownInput";
import TextArea from "../../components/Inputs/TextAreaInput";
import { getSessions } from "../../services/sessionService";
import {
  createEvent,
  deleteEvent,
  getEvents,
  updateEvent,
} from "../../services/eventService";
import DateInput from "../../components/Inputs/DateInput";
import { eventSchema } from "../../validators/eventValidators";
import { format } from "date-fns";
import ImageFileUpload from "../../components/Inputs/ImageFileUpload";
import { VITE_API_BASE_URL } from "../../config";
import { Link } from "react-router-dom";
import { BiShow } from "react-icons/bi";
import {
  EnvironmentOutlined,
  CalendarOutlined,
  TeamOutlined,
  TagOutlined,
  UserOutlined,
  FileImageOutlined,
} from "@ant-design/icons";

const Event = () => {
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [eventToEdit, setEventToEdit] = useState(null);
  const [modalText, setModalText] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errs, setErrs] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    loadEvents();
    loadSessions();
  }, []);

  const statusOptions = [
    { id: "upcoming", name: "Upcoming" },
    { id: "ongoing", name: "Ongoing" },
    { id: "completed", name: "Completed" },
  ];

  useEffect(() => {
    let filtered = events;
    if (searchTerm.trim() !== "") {
      filtered = events.filter((item) =>
        `${item?.title} ${item?.description} ${item?.location}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
    }

    setFilteredEvents(filtered);
  }, [searchTerm, events]);

  const loadSessions = async () => {
    try {
      const { data } = await getSessions();
      setSessions(data.data);
    } catch (error) {
      toast.error("Failed to load sessions");
    }
  };

  const loadEvents = async () => {
    setLoading(true);
    try {
      const { data } = await getEvents();

      setEvents(data.data);
      setFilteredEvents(data.data);
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (payload, resetForm) => {
    if (eventToEdit) {
      onUpdateEvent(payload, resetForm);
    } else {
      onCreateEvent(payload, resetForm);
    }
  };

  const onCreateEvent = async (payload, resetForm) => {
    setErrs(null);

    try {
      const { data } = await createEvent(payload);

      setEvents((prev) => [...prev, data.data]);

      toast.success("Event added successfully");

      setIsModalOpen(false);

      resetForm();
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(response.data.error || "Event was not added, try again!");
      }
    }
  };

  const handleEdit = (category) => {
    setEventToEdit(category);
    setIsModalOpen(true);
  };

  const onUpdateEvent = async (payload, resetForm) => {
    setErrs(null);

    const formData = new FormData();

    formData.append("_method", "PATCH");
    formData.append("title", payload.title);
    formData.append("description", payload.description);
    formData.append("location", payload.location);
    formData.append("status", payload.status);
    formData.append("host", payload.host);
    formData.append("start_date", payload.start_date);
    formData.append("end_date", payload.end_date);
    formData.append("session_id", payload.session_id);
    formData.append("registration_link", payload.registration_link);

    if (typeof payload.image !== "string") {
      formData.append("image", payload.image);
    }

    try {
      const originalEvents = [...events];

      const eventIndex = originalEvents.findIndex(
        (event) => event.id === eventToEdit.id
      );

      if (eventIndex === -1) throw new Error("Events not found.");

      const { data } = await updateEvent(eventToEdit.id, formData);

      originalEvents[eventIndex] = data.data;

      setEvents(originalEvents);

      setEventToEdit(null);

      resetForm();

      setIsModalOpen(false);

      toast.success("Event updated successfully!");
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(response?.data?.error || "Event not updated, try again!");
      }
    }
  };

  const handleDelete = (payload) => {
    setModalText(
      `Please confirm that you want to delete %${payload?.title?.toUpperCase()}`
    );
    setEventToDelete(payload);
    setIsDeleteModalOpen(true);
  };

  const onDeleteEvent = async () => {
    const originalEvents = [...events];

    const newEvents = originalEvents.filter(
      (event) => event.id !== eventToDelete.id
    );

    setEvents(newEvents);

    try {
      await deleteEvent(eventToDelete?.id);

      toast.success("Event deleted successfully!");
    } catch (error) {
      const { response } = error;
      toast.error(
        ` ${
          response?.data?.message ||
          error?.response?.data?.error ||
          "Event not deleted, try again later"
        }`
      );

      setEvents(originalEvents);
    } finally {
      setIsDeleteModalOpen(false);

      setEventToDelete(null);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsDeleteModalOpen(false);
    setEventToEdit(null);
    setEventToDelete(null);
    setErrs(null);
  };

  const getStatusBadge = (status) => {
    const normalized = status?.toLowerCase();

    const statusStyles = {
      upcoming: {
        color: "#1a73e8",
        backgroundColor: "#e8f0fe",
      },
      ongoing: {
        color: "#137333",
        backgroundColor: "#e6f4ea",
      },
      completed: {
        color: "#5f6368",
        backgroundColor: "#f1f3f4",
      },
    };

    const defaultStyle = {
      color: "#5f6368",
      backgroundColor: "#f1f3f4",
    };

    const style = {
      display: "inline-block",
      padding: "4px 8px",
      borderRadius: "6px",
      fontWeight: 500,
      fontSize: "0.7rem",
      textTransform: "capitalize",
      ...(statusStyles[normalized] || defaultStyle),
    };

    const displayText =
      normalized?.charAt(0).toUpperCase() + normalized?.slice(1);

    return <span style={style}>{displayText || "N/A"}</span>;
  };

  const columns = [
    {
      title: "#",
      render: (text, record, index) => index + 1,
      width: 70,
    },

    {
      title: "Title",
      dataIndex: "title",
      key: "title",
    },

    {
      title: "Location",
      dataIndex: "location",
      key: "location",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => getStatusBadge(status),
    },
    {
      title: "Start date",
      dataIndex: "start_date",
      key: "start_date",
      render: (dateStr) => {
        if (!dateStr) return "N/A";

        try {
          return format(new Date(dateStr), "do MMMM yyyy");
        } catch {
          return "Invalid date";
        }
      },
    },
    {
      title: "End date",
      dataIndex: "end_date",
      key: "end_date",
      render: (dateStr) => {
        if (!dateStr) return "N/A";

        try {
          return format(new Date(dateStr), "do MMMM yyyy");
        } catch {
          return "Invalid date";
        }
      },
    },
    {
      title: "Year",
      dataIndex: "session",
      key: "session",
      render: (_, record) => record?.session?.name,
    },

    {
      title: "Action",
      key: "action",
      width: 160,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="View Event">
            <button
              className="p-2 border border-blue-200 rounded-md text-blue-600 hover:bg-blue-50"
              onClick={() => {
                setSelectedEvent(record);
                setIsDrawerOpen(true);
              }}
            >
              <BiShow size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Edit Faculty">
            <button
              className="p-2 border border-green-200 rounded-md text-green-600 hover:bg-green-50 transition cursor-pointer"
              onClick={() => handleEdit(record)}
            >
              <FiEdit size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Delete Faculty">
            <button
              className="p-2 border border-red-200 rounded-md text-red-500 hover:bg-red-50 transition cursor-pointer"
              onClick={() => handleDelete(record)}
            >
              <FiTrash2 size={16} />
            </button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeading title="Events" subtitle1="Events" />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Events"
            count={filteredEvents.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            buttonLabel="+ Add Event"
            onButtonClick={() => setIsModalOpen(true)}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={filteredEvents.map((item) => ({
              ...item,
              key: item.id,
            }))}
          />

          {/* Modal */}

          <Modal
            title={
              <div className="px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  {eventToEdit ? "Edit Event" : "Add Event"}
                </h2>
              </div>
            }
            open={isModalOpen}
            onCancel={handleCancel}
            footer={false}
            width={800}
          >
            <Formik
              initialValues={{
                title: eventToEdit ? eventToEdit?.title : "",
                description: eventToEdit ? eventToEdit?.description : "",
                location: eventToEdit ? eventToEdit?.location : "",
                status: eventToEdit ? eventToEdit?.status : "",
                host: eventToEdit ? eventToEdit?.host : "",
                start_date: eventToEdit ? eventToEdit?.start_date : "",
                end_date: eventToEdit ? eventToEdit?.end_date : "",
                session_id: eventToEdit ? eventToEdit?.session?.id : "",
                image: eventToEdit ? eventToEdit?.image : "",
                registration_link: eventToEdit
                  ? eventToEdit?.registration_link
                  : "",
              }}
              enableReinitialize
              validationSchema={eventSchema}
              onSubmit={(values, { resetForm }) => {
                onSubmit(values, resetForm);
              }}
            >
              {({
                values,
                handleChange,
                handleBlur,
                errors,
                touched,
                setFieldValue,
                setFieldTouched,
                submitForm,
              }) => {
                return (
                  <Form className="space-y-5 px-4! py-4!">
                    {errs && <FormErrors errors={errs} />}

                    <div className="grid grid-cols-1">
                      {eventToEdit?.image && (
                        <div className="flex justify-center items-center w-full">
                          <div className="h-40 w-40">
                            <img
                              className="w-54 text-sm"
                              src={`${VITE_API_BASE_URL}/files/events/${eventToEdit?.image}`}
                              alt={`Event Photo`}
                            />
                          </div>
                        </div>
                      )}
                      <ImageFileUpload
                        title="Event Photo"
                        name="image"
                        requiredField={false}
                        errors={errors}
                        acceptedFiles="JPG, PNG up to 2 MB"
                        handleChange={handleChange}
                        setFieldValue={setFieldValue}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <TextInput
                        name="title"
                        title="Title"
                        placeholder="e.g. UVCF Annual Innovation Expo"
                        requiredField
                        errors={errors}
                        values={values}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                      />

                      <TextInput
                        name="host"
                        title="Host"
                        placeholder="e.g. Ministry of Science, Technology and Innovation"
                        requiredField
                        errors={errors}
                        values={values}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                      />

                      <TextInput
                        name="location"
                        title="Location"
                        placeholder="e.g. Innovation Hub – Block B, MUBS"
                        requiredField
                        errors={errors}
                        values={values}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                      />
                    </div>

                    <TextArea
                      name="description"
                      title="Description"
                      placeholder="e.g. An annual event showcasing student-led engineering projects, prototypes, and tech startups developed under university mentorship."
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <TextInput
                        name="registration_link"
                        title="Registration Link"
                        placeholder="Enter Registration Link"
                        errors={errors}
                        values={values}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                      />
                      <CustomDropDownInput
                        name="status"
                        title="Status"
                        placeholder="Select a status"
                        requiredField
                        errors={errors}
                        values={values}
                        touched={touched}
                        displayLabel="name"
                        idLabel="id"
                        options={statusOptions}
                        setFieldValue={setFieldValue}
                      />

                      <CustomDropDownInput
                        name="session_id"
                        title="Year"
                        placeholder="Select a session"
                        requiredField
                        errors={errors}
                        values={values}
                        touched={touched}
                        displayLabel="name"
                        idLabel="id"
                        options={sessions}
                        setFieldValue={setFieldValue}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <DateInput
                        name="start_date"
                        title="Start date"
                        placeholder="Select start date"
                        requiredField={true}
                        errors={errors}
                        values={values}
                        touched={touched}
                        setFieldValue={setFieldValue}
                        setFieldTouched={setFieldTouched}
                      />

                      <DateInput
                        name="end_date"
                        title="End date"
                        placeholder="Select end date"
                        requiredField={true}
                        errors={errors}
                        values={values}
                        touched={touched}
                        setFieldValue={setFieldValue}
                        setFieldTouched={setFieldTouched}
                      />
                    </div>

                    <div className="flex justify-end gap-2">
                      <FormCancelButton text="Cancel" onClick={handleCancel} />
                      <FormButton
                        text="Save"
                        type="primary"
                        onClick={submitForm}
                      />
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </Modal>

          <DeleteModal
            open={isDeleteModalOpen}
            handleOk={onDeleteEvent}
            handleCancel={handleCancel}
            confirmLoading={confirmLoading}
            modalText={modalText}
            extra={
              <div className="mt-3 bg-red-100 text-red-500 border-[1px] border-red-300 mx-5 px-3 py-2 rounded-md">
                <h3 className="text-red-700 text-md flex gap-1 items-center">
                  <IoIosWarning /> Warning
                </h3>
                Please note that this action cannot be reversed!
              </div>
            }
          />

          <Drawer
            title={
              <div className="flex items-center gap-3">
                <TagOutlined className="text-indigo-600" />
                <span className="text-lg font-semibold text-gray-800">
                  {selectedEvent?.title || "Event Details"}
                </span>
              </div>
            }
            width={550}
            onClose={() => setIsDrawerOpen(false)}
            open={isDrawerOpen}
            placement="right"
          >
            {selectedEvent ? (
              <div className="text-sm text-gray-700 space-y-6 px-1">
                {/* Grid layout for metadata */}
                <div className="grid grid-cols-1 gap-y-3 sm:grid-cols-2 sm:gap-x-4">
                  <div className="flex items-center gap-2">
                    <EnvironmentOutlined className="text-orange-500" />
                    <span>
                      <strong>Location:</strong> {selectedEvent.location || "—"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <UserOutlined className="text-green-600" />
                    <span>
                      <strong>Host:</strong> {selectedEvent.host || "—"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <TagOutlined className="text-blue-600" />
                    <span>
                      <strong>Status:</strong> {selectedEvent.status || "—"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CalendarOutlined className="text-indigo-500" />
                    <span>
                      <strong>Start:</strong>{" "}
                      {selectedEvent.start_date
                        ? format(
                            new Date(selectedEvent.start_date),
                            "do MMM yyyy"
                          )
                        : "—"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CalendarOutlined className="text-indigo-500" />
                    <span>
                      <strong>End:</strong>{" "}
                      {selectedEvent.end_date
                        ? format(
                            new Date(selectedEvent.end_date),
                            "do MMM yyyy"
                          )
                        : "—"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <TeamOutlined className="text-rose-500" />
                    <span>
                      <strong>Year:</strong>{" "}
                      {selectedEvent.session?.name || "—"}
                    </span>
                  </div>
                </div>

                <Divider />

                {/* Description */}
                <div className="border border-amber-400 pb-2 bg-yellow-300/20 rounded-md px-3 py-2">
                  <h4 className="text-sm font-semibold text-gray-600 mb-1">
                    Description
                  </h4>
                  <p className="text-gray-700">
                    {selectedEvent.description || "—"}
                  </p>
                </div>

                {/* Registration */}
                {selectedEvent.registration_link && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-600 mb-1">
                      Registration
                    </h4>
                    <a
                      href={selectedEvent.registration_link}
                      target="_blank"
                      rel="noreferrer"
                      className="text-indigo-600 underline"
                    >
                      Register Here
                    </a>
                  </div>
                )}

                {/* Image */}
                {selectedEvent.image && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
                      <FileImageOutlined className="text-gray-500" />
                      Event Banner
                    </h4>
                    <img
                      src={`${VITE_API_BASE_URL}/files/events/${selectedEvent.image}`}
                      alt="Event"
                      className="w-full h-auto rounded-md border object-cover"
                    />
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500">No event selected.</p>
            )}
          </Drawer>
        </div>
      </div>
    </>
  );
};

export default Event;
